(() => {
  const FRAME_SELECTOR = '[data-product-image-frame]';
  const PRIMARY_SELECTOR = '.product-image-primary, [data-product-fit-image]';
  const IMAGE_SELECTOR = '.product-image-primary, .product-image-secondary, [data-product-fit-image]';
  const STORAGE_KEY = 'michaela_image_bounds_v1';
  const CACHE_LIMIT = 256;
  const CACHE_AGE = 30 * 24 * 60 * 60 * 1000;
  const boundsCache = new Map();
  const analysisCache = new Map();
  const analysisQueue = [];
  const frameFitters = new WeakMap();
  const scheduledImages = new WeakSet();
  let activeAnalyses = 0;
  let drainScheduled = false;
  let saveTimer = 0;

  // Cache only geometry, keyed by the full image URL (including Shopify's v=
  // revision). Settings such as fill and manual zoom are applied separately.
  const validBounds = (bounds) => bounds === null || (
    bounds && ['width', 'height', 'left', 'top', 'right', 'bottom'].every((key) => Number.isFinite(bounds[key])) &&
    bounds.width >= 16 && bounds.width <= 256 && bounds.height >= 16 && bounds.height <= 256 &&
    bounds.left >= 0 && bounds.top >= 0 && bounds.right <= bounds.width && bounds.bottom <= bounds.height &&
    bounds.right > bounds.left && bounds.bottom > bounds.top
  );

  const cacheKey = (src) => {
    try { return new URL(src, document.baseURI).href; } catch (_) { return src; }
  };
  const persistentKey = (key) => /^https?:\/\//.test(key) && key.length <= 2048;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const entries = stored && stored.length < 750000 ? JSON.parse(stored) : [];
    if (Array.isArray(entries)) {
      entries.slice(-CACHE_LIMIT).forEach((entry) => {
        if (!Array.isArray(entry) || entry.length !== 2) return;
        const [key, record] = entry;
        if (typeof key !== 'string' || !persistentKey(key) || !record) return;
        const age = Date.now() - record.time;
        if (!Number.isFinite(age) || age < 0 || age >= CACHE_AGE || !validBounds(record.bounds)) return;
        boundsCache.set(key, record);
      });
    }
  } catch (_) {
    // Storage can be blocked, full or contain data from an interrupted write.
  }

  const saveBounds = () => {
    window.clearTimeout(saveTimer);
    saveTimer = 0;
    try {
      const entries = [...boundsCache].filter(([key]) => persistentKey(key));
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (_) {
      // Keep the in-memory cache working when persistent storage is unavailable.
    }
  };

  const rememberBounds = (key, bounds) => {
    boundsCache.delete(key);
    boundsCache.set(key, { bounds, time: Date.now() });
    if (boundsCache.size > CACHE_LIMIT) boundsCache.delete(boundsCache.keys().next().value);
    if (persistentKey(key) && !saveTimer) saveTimer = window.setTimeout(saveBounds, 150);
  };

  window.addEventListener('pagehide', () => { if (saveTimer) saveBounds(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && saveTimer) saveBounds();
  });

  const clamp = (value, min, max, fallback) => {
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
  };

  // Inspect a small copy only. Photos without a clean white/transparent border
  // are left alone; this is whitespace detection, not subject recognition.
  const findProductBounds = (source) => {
    const longestSide = Math.max(source.naturalWidth, source.naturalHeight);
    if (!longestSide) return null;
    const ratio = Math.min(1, 256 / longestSide);
    const width = Math.round(source.naturalWidth * ratio);
    const height = Math.round(source.naturalHeight * ratio);
    if (Math.min(width, height) < 16) return null;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Image analysis canvas unavailable');
    context.drawImage(source, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);

    const isWhite = (offset, threshold) => {
      const alpha = data[offset + 3] / 255;
      const red = 255 + (data[offset] - 255) * alpha;
      const green = 255 + (data[offset + 1] - 255) * alpha;
      const blue = 255 + (data[offset + 2] - 255) * alpha;
      const low = Math.min(red, green, blue);
      return low >= threshold && Math.max(red, green, blue) - low <= 10;
    };

    let borderPixels = 0;
    let transparentBorder = 0;
    let whiteBorder = 0;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        if (x > 1 && y > 1 && x < width - 2 && y < height - 2) continue;
        const offset = (y * width + x) * 4;
        borderPixels += 1;
        if (data[offset + 3] <= 8) transparentBorder += 1;
        if (isWhite(offset, 247)) whiteBorder += 1;
      }
    }
    const transparent = transparentBorder / borderPixels >= 0.98;
    if (!transparent && whiteBorder / borderPixels < 0.98) return null;

    let left = width;
    let top = height;
    let right = -1;
    let bottom = -1;
    let foregroundPixels = 0;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const offset = (y * width + x) * 4;
        const background = transparent ? data[offset + 3] <= 16 : isWhite(offset, 246);
        if (background) continue;
        foregroundPixels += 1;
        left = Math.min(left, x);
        top = Math.min(top, y);
        right = Math.max(right, x);
        bottom = Math.max(bottom, y);
      }
    }

    // Blank/very faint photos and subjects touching the edge are ambiguous.
    if (foregroundPixels < Math.max(24, width * height * 0.005)) return null;
    if (left <= 1 || top <= 1 || right >= width - 2 || bottom >= height - 2) return null;
    const padding = 2;
    left = Math.max(0, left - padding);
    top = Math.max(0, top - padding);
    right = Math.min(width, right + 1 + padding);
    bottom = Math.min(height, bottom + 1 + padding);
    return { width, height, left, top, right, bottom };
  };

  const drainAnalysisQueue = () => {
    drainScheduled = false;
    analysisQueue.sort((a, b) => a.priority - b.priority);
    while (activeAnalyses < 2 && analysisQueue.length) {
      // Leave a slot free when only hover images/thumbnails are waiting.
      if (analysisQueue[0].priority === 2 && activeAnalyses > 0) break;
      activeAnalyses += 1;
      const job = analysisQueue.shift();
      const { src, resolve } = job;
      const source = new Image();
      let settled = false;
      const finishAnalysis = (bounds, analyzed = false) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        source.onload = null;
        source.onerror = null;
        job.inspect = null;
        activeAnalyses -= 1;
        // A valid "no safe crop" result is reusable; network/CORS failures are not.
        if (analyzed) rememberBounds(src, bounds);
        resolve(bounds);
        scheduleAnalysis();
      };
      const timeout = window.setTimeout(() => finishAnalysis(null), 5000);
      source.onload = () => {
        const inspect = () => {
          if (settled) return;
          try {
            finishAnalysis(findProductBounds(source), true);
          } catch (_) {
            // CORS restrictions or unavailable canvas must never hide a photo.
            finishAnalysis(null);
          }
        };
        job.inspect = inspect;
        if (job.priority === 0) {
          inspect();
        } else if ('requestIdleCallback' in window) {
          window.requestIdleCallback(inspect, { timeout: 300 });
        } else {
          window.setTimeout(inspect, 0);
        }
      };
      source.onerror = () => finishAnalysis(null);
      // Keep CORS off the visible image: a failed analysis request cannot break it.
      source.crossOrigin = 'anonymous';
      source.fetchPriority = job.priority === 0 ? 'auto' : 'low';
      source.src = src;
    }
  };

  // Batch one observer callback before starting requests so all visible primary
  // images enter the queue ahead of secondary images on the first card.
  const scheduleAnalysis = () => {
    if (drainScheduled) return;
    drainScheduled = true;
    queueMicrotask(drainAnalysisQueue);
  };

  const analyzeImage = (source, priority) => {
    const src = cacheKey(source);
    if (boundsCache.has(src)) return Promise.resolve(boundsCache.get(src).bounds);
    if (!analysisCache.has(src)) {
      const job = { src, priority, inspect: null };
      job.result = new Promise((resolve) => { job.resolve = resolve; });
      analysisQueue.push(job);
      analysisCache.set(src, job);
      if (analysisCache.size > 128) analysisCache.delete(analysisCache.keys().next().value);
    } else {
      const job = analysisCache.get(src);
      job.priority = Math.min(job.priority, priority);
      if (job.priority === 0) job.inspect?.();
    }
    scheduleAnalysis();
    return analysisCache.get(src).result;
  };

  // Keep the same subject framing across cards, galleries and smaller thumbnails.
  const positionImage = (image, width, height, left, top, originX = 50, originY = 50) => {
    image.style.setProperty('--card-image-width', `${width.toFixed(4)}%`);
    image.style.setProperty('--card-image-height', `${height.toFixed(4)}%`);
    image.style.setProperty('--card-image-left', `${left.toFixed(4)}%`);
    image.style.setProperty('--card-image-top', `${top.toFixed(4)}%`);
    image.style.setProperty('--card-image-origin', `${originX.toFixed(4)}% ${originY.toFixed(4)}%`);
    // Shopify may supply an inline focal point; our explicit crop supplies its own.
    image.style.objectPosition = 'center';
    image.classList.add('is-product-image-fitted');
  };

  const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver((entries) => {
    entries.forEach(({ target }) => frameFitters.get(target)?.forEach((applyFit) => applyFit()));
  }) : null;

  const imagePriority = (frame, image) => {
    if (image.matches('.product-image-secondary') || frame.matches('[data-thumb]')) return 2;
    const rect = frame.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth ? 0 : 1;
  };

  const fitFrame = (frame, cachedOnly = false) => {
    if (!frameFitters.has(frame)) {
      frameFitters.set(frame, []);
      resizeObserver?.observe(frame);
    }
    const fitters = frameFitters.get(frame);
    frame.querySelectorAll(IMAGE_SELECTOR).forEach((image) => {
      if (scheduledImages.has(image)) return;
      if (frame.dataset.imageFit === 'manual') {
        scheduledImages.add(image);
        const applyManualFit = () => {
          const width = image.naturalWidth || Number(image.getAttribute('width'));
          const height = image.naturalHeight || Number(image.getAttribute('height'));
          if (!width || !height || !frame.clientWidth || !frame.clientHeight) return;
          const frameRatio = frame.clientWidth / frame.clientHeight;
          const zoom = clamp(frame.dataset.imageZoom, 50, 300, 100) / 100;
          const factor = 90 * zoom / Math.max(width / frameRatio, height);
          const x = clamp(frame.dataset.imageX, -50, 50, 0);
          const y = clamp(frame.dataset.imageY, -50, 50, 0);
          positionImage(image, width * factor / frameRatio, height * factor,
            (100 - width * factor / frameRatio) / 2 + x, (100 - height * factor) / 2 + y);
        };
        fitters.push(applyManualFit);
        applyManualFit();
        if (!image.complete) image.addEventListener('load', applyManualFit, { once: true });
        return;
      }

      const src = image.dataset.imageFitSrc;
      if (!src) return;
      if (cachedOnly && !boundsCache.has(cacheKey(src))) return;
      scheduledImages.add(image);
      analyzeImage(src, imagePriority(frame, image)).then((bounds) => {
        if (!bounds) return;
        const applyAutoFit = () => {
          if (!frame.clientWidth || !frame.clientHeight) return;
          const frameRatio = frame.clientWidth / frame.clientHeight;
          const { width, height, left, top, right, bottom } = bounds;
          const fill = clamp(frame.dataset.imageFill, 75, 95, 88);
          // Respect rectangular gallery frames and cap magnification at 3x.
          const factor = Math.min(fill / Math.max((right - left) / frameRatio, bottom - top),
            270 / Math.max(width / frameRatio, height));
          const centerX = (left + right) / 2;
          const centerY = (top + bottom) / 2;
          positionImage(image, width * factor / frameRatio, height * factor,
            50 - centerX * factor / frameRatio, 50 - centerY * factor,
            centerX / width * 100, centerY / height * 100);
        };
        fitters.push(applyAutoFit);
        applyAutoFit();
      });
    });
  };

  const fitObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      fitObserver.unobserve(target);
      fitFrame(target);
    });
  }, { rootMargin: '200px' }) : null;

  const finish = (frame, state = 'is-loaded') => {
    frame.classList.remove('is-loading');
    frame.classList.add(state);
  };

  const initializeFrame = (frame) => {
    if (frame.dataset.imageLoaderReady === 'true') {
      if (frameFitters.has(frame)) resizeObserver?.observe(frame);
      if (frame.dataset.imageFit === 'auto') fitObserver?.observe(frame);
      return;
    }
    frame.dataset.imageLoaderReady = 'true';

    const image = frame.querySelector(PRIMARY_SELECTOR);
    if (!image) {
      finish(frame);
      return;
    }

    if (frame.dataset.imageFit === 'auto' || frame.dataset.imageFit === 'manual') {
      if (fitObserver && frame.dataset.imageFit === 'auto') {
        // Cached crops are applied immediately, before waiting for visibility.
        fitFrame(frame, true);
        fitObserver.observe(frame);
      } else {
        fitFrame(frame);
      }
    }

    if (frame.dataset.imageFit === 'auto' && frame.querySelector('.product-image-secondary')) {
      const prioritizeHover = () => {
        fitFrame(frame);
        const src = frame.querySelector('.product-image-secondary')?.dataset.imageFitSrc;
        if (src) analyzeImage(src, 0);
      };
      frame.addEventListener('pointerenter', prioritizeHover, { once: true });
      frame.addEventListener('focusin', prioritizeHover, { once: true });
    }

    frame.classList.add('is-loading');

    if (image.complete) {
      finish(frame, image.naturalWidth > 0 ? 'is-loaded' : 'is-error');
      return;
    }

    image.addEventListener('load', () => finish(frame), { once: true });
    image.addEventListener('error', () => finish(frame, 'is-error'), { once: true });

    window.setTimeout(() => finish(frame), 8000);
  };

  const initializeProductImages = (root = document) => {
    if (root.matches?.(FRAME_SELECTOR)) initializeFrame(root);
    root.querySelectorAll?.(FRAME_SELECTOR).forEach(initializeFrame);
  };

  const stopObserving = (root) => {
    const stop = (frame) => {
      fitObserver?.unobserve(frame);
      resizeObserver?.unobserve(frame);
    };
    if (root.matches?.(FRAME_SELECTOR)) stop(root);
    root.querySelectorAll?.(FRAME_SELECTOR).forEach(stop);
  };

  initializeProductImages();

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType === 1 && !node.isConnected) stopObserving(node);
      });
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) initializeProductImages(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
